import {
  Box,
  Button,
  Checkbox,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import {
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { DataGrid } from "@/components/DataGrid/DataGrid";
import { ResponsiveTable } from "@/components/Responsive";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MIN_TAP_TARGET } from "@/styles/responsive";
import { type InputRow, type MatchedRow } from "./types";
import { StatusChip } from "./StatusChip";
import { toRowView, type RowView } from "./rowView";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

interface Props {
  /** 絞り込み後の行。ページ送りはこの一覧に対して行う */
  rows: MatchedRow[];
  inputs: Map<number, InputRow>;
  selected: number[];
  onSelectedChange: (rowNumbers: number[]) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  disabled: boolean;
  resolve: (rowNumber: number) => void;
}

interface GridRow extends RowView {
  id: number;
  source: MatchedRow;
}

const Judgement = ({ row }: { row: GridRow }): JSX.Element => (
  <Box sx={{ py: 1 }}>
    <StatusChip status={row.source.status} />
    {row.confidence && (
      <Typography variant="caption" sx={{ ml: 1 }}>
        {row.confidence}
      </Typography>
    )}
    {row.reasons.map((reason) => (
      <Typography
        key={reason}
        variant="caption"
        display="block"
        color="text.secondary"
      >
        {reason}
      </Typography>
    ))}
    {row.source.duplicateOfRowNumber && (
      <Typography variant="caption" display="block">
        重複元: No.{row.source.duplicateOfRowNumber}
      </Typography>
    )}
  </Box>
);

/**
 * 法人の確認画面の一覧。PCは行選択・ページ送り付きのDataGrid、SPはカード表示（レスポンシブ基盤ガイド）。
 * 選択とページの状態は親で1つ持ち、PCとSPの両方へ渡す
 */
export const ResultsTable = ({
  rows,
  inputs,
  selected,
  onSelectedChange,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  disabled,
  resolve,
}: Props): JSX.Element => {
  const isMobile = useIsMobile();
  const gridRows: GridRow[] = rows.map((row) => ({
    ...toRowView(row, inputs.get(row.rowNumber)),
    id: row.rowNumber,
    source: row,
  }));
  const actionButton = (row: GridRow): JSX.Element => (
    <Button
      size="small"
      disabled={disabled}
      sx={{ minHeight: isMobile ? MIN_TAP_TARGET : undefined }}
      onClick={() => {
        resolve(row.rowNumber);
      }}
    >
      {row.action}
    </Button>
  );

  if (isMobile) {
    const pageRows = gridRows.slice(page * pageSize, (page + 1) * pageSize);
    const toggle = (rowNumber: number, checked: boolean): void => {
      onSelectedChange(
        checked
          ? [...selected, rowNumber]
          : selected.filter((value) => value !== rowNumber)
      );
    };
    return (
      <Stack spacing={2}>
        <ResponsiveTable
          title="法人リスト突合結果"
          headers={[
            "選択",
            "法人名",
            "ステータス",
            "取得対象",
            "市区町村",
            "法人番号",
            "会社法人等番号",
            "操作",
          ]}
          summaryColumns={[0, 1, 2, 3, 7]}
          getRowId={(_, index) => pageRows[index].rowNumber}
          rows={pageRows.map((row) => [
            <Checkbox
              key="select"
              checked={selected.includes(row.rowNumber)}
              disabled={disabled}
              inputProps={{ "aria-label": `No.${row.rowNumber}を選択` }}
              sx={{ width: MIN_TAP_TARGET, height: MIN_TAP_TARGET }}
              onChange={(event) => {
                toggle(row.rowNumber, event.target.checked);
              }}
            />,
            `No.${row.rowNumber} ${row.companyName}`,
            <Judgement key="status" row={row} />,
            row.target,
            row.city,
            row.corporateNumber,
            row.corporateIdentificationNumber,
            actionButton(row),
          ])}
        />
        {rows.length > pageSize && (
          <Pagination
            count={Math.ceil(rows.length / pageSize)}
            page={page + 1}
            onChange={(_, next) => {
              onPageChange(next - 1);
            }}
            sx={{
              "& .MuiPaginationItem-root": {
                minWidth: MIN_TAP_TARGET,
                height: MIN_TAP_TARGET,
              },
            }}
          />
        )}
      </Stack>
    );
  }

  // 1366px幅の画面（サイドバー込み）で横スクロールせずに操作列まで見えるよう、最小幅の合計を約1,040pxに抑える
  const columns: Array<GridColDef<GridRow>> = [
    { field: "rowNumber", headerName: "No", width: 60 },
    {
      field: "companyName",
      headerName: "法人名",
      flex: 1.4,
      minWidth: 180,
      renderCell: ({ row }: GridRenderCellParams<unknown, GridRow>) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            {row.companyName}
          </Typography>
          {row.source.corporation &&
            row.inputCompanyName &&
            row.inputCompanyName !== row.companyName && (
              <Typography variant="caption" display="block">
                入力: {row.inputCompanyName}
              </Typography>
            )}
        </Box>
      ),
    },
    { field: "city", headerName: "市区町村", flex: 1, minWidth: 110 },
    { field: "corporateNumber", headerName: "法人番号", width: 130 },
    {
      field: "corporateIdentificationNumber",
      headerName: "会社法人等番号",
      width: 130,
    },
    {
      field: "status",
      headerName: "ステータス",
      flex: 1.2,
      minWidth: 190,
      renderCell: ({ row }: GridRenderCellParams<unknown, GridRow>) => (
        <Judgement row={row} />
      ),
    },
    { field: "target", headerName: "取得対象", width: 80 },
    {
      field: "action",
      headerName: "操作",
      width: 110,
      sortable: false,
      renderCell: ({ row }: GridRenderCellParams<unknown, GridRow>) =>
        actionButton(row),
    },
  ];
  return (
    <DataGrid
      aria-label="法人リスト突合結果"
      rows={gridRows}
      columns={columns}
      getRowHeight={() => "auto"}
      checkboxSelection
      disableSelectionOnClick
      keepNonExistentRowsSelected
      selectionModel={selected}
      onSelectionModelChange={(model) => {
        onSelectedChange(model.map(Number));
      }}
      pagination
      page={page}
      pageSize={pageSize}
      rowsPerPageOptions={PAGE_SIZE_OPTIONS}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    />
  );
};
